import pixroLogo from "@/assets/pixro_text_logo.svg";
import AiTeamMenu from "@/components/AiTeamMenu";
import AiToolsMenu from "@/components/AiToolsMenu";
import ResourcesMenu from "@/components/ResourcesMenu";
import SolutionsMenu from "@/components/SolutionsMenu";
import { aiCrew as aiTeamAgents } from "@/lib/agents";
import { defaultPixroBookingConfig, getCalTriggerProps } from "@/lib/cal";
import { APP_URL } from "@/lib/config";
import { toolAppHref, toolLandingHref } from "@/lib/tools";
import {
    ArrowRight,
    BookOpen,
    Car,
    ChevronDown,
    CircleUserRound,
    Clapperboard,
    Code2,
    Crop,
    Eraser,
    Film,
    Gem,
    Image as ImageIcon,
    Info,
    Mail,
    Maximize2,
    Menu,
    MessageSquareText,
    Paintbrush,
    Pencil,
    Shirt,
    ShoppingBag,
    Sparkles,
    Store,
    Video,
    X,
    ZoomIn,
    type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
    { label: "Tools", hasDropdown: true },
    { label: "AI Team", hasDropdown: true },
    { label: "Solutions", hasDropdown: true },
    { label: "Resources", hasDropdown: true },
    { label: "Pricing", hasDropdown: false },
];


type MobileLink = {
    label: string;
    href: string;
    icon: LucideIcon;
    external?: boolean;
    comingSoon?: boolean;
};
type MobileGroup = { title: string; links: MobileLink[] };

const aiToolsMobile: MobileGroup[] = [
    {
        title: "Videos",
        links: [
            {
                label: "Product videos",
                href: toolLandingHref("Product videos"),
                icon: Clapperboard,
            },
            {
                label: "Talking avatars",
                href: toolAppHref("Talking avatars"),
                icon: CircleUserRound,
                comingSoon: true,
            },
            {
                label: "UGC videos",
                href: toolLandingHref("UGC videos"),
                icon: Video,
            },
            {
                label: "Upscale video",
                href: toolAppHref("Upscale video"),
                icon: Maximize2,
                comingSoon: true,
            },
            {
                label: "Caption videos",
                href: toolAppHref("Caption videos"),
                icon: MessageSquareText,
                comingSoon: true,
            },
            {
                label: "AI Fashion reels",
                href: toolLandingHref("AI Fashion reels"),
                icon: Film,
            },
            {
                label: "CGI videos",
                href: toolAppHref("CGI videos"),
                icon: Sparkles,
                comingSoon: true,
            },
        ],
    },
    {
        title: "Photoshoot",
        links: [
            {
                label: "Background replacer",
                href: toolLandingHref("Background replacer"),
                icon: ImageIcon,
            },
            //   {
            //     label: "Product showcase",
            //     href: toolAppHref("Product showcase"),
            //     icon: Storefront,
            //     external: true,
            //   },
            {
                label: "Virtual Try-On",
                href: toolLandingHref("Virtual Try-On"),
                icon: Shirt,
            },
            {
                label: "Ghost mannequin",
                href: toolLandingHref("Ghost mannequin"),
                icon: Paintbrush,
            },
            {
                label: "Flat-lay",
                href: toolLandingHref("Flat-lay"),
                icon: Store,
            },
        ],
    },
    {
        title: "Edit",
        links: [
            {
                label: "Remove background",
                href: toolLandingHref("Remove background"),
                icon: Eraser,
            },
            {
                label: "Upscale",
                href: toolLandingHref("Upscale"),
                icon: ZoomIn,
            },
            {
                label: "Resize",
                href: toolLandingHref("Resize"),
                icon: Crop,
            },
            {
                label: "Edit with AI",
                href: toolLandingHref("Edit with AI"),
                icon: Pencil,
            },
        ],
    },
];

const solutionsMobile: MobileGroup[] = [
    {
        title: "Solutions",
        links: [
            { label: "Fashion", href: "/solutions/fashion", icon: Shirt },
            {
                label: "Ecommerce",
                href: "/solutions/ecommerce",
                icon: Store,
            },
            {
                label: "Accessories",
                href: "/solutions/accessories",
                icon: ShoppingBag,
            },
            { label: "Jewellery", href: "/solutions/jewellery", icon: Gem },
            { label: "Automobile", href: "/solutions/automobile", icon: Car },
        ],
    },
];


const resourcesMobile: MobileGroup[] = [
    {
        title: "Resources",
        links: [
            { label: "About", href: "/about", icon: Info },
            { label: "Blogs", href: "/blogs", icon: BookOpen },
            { label: "APIs", href: "/apis", icon: Code2 },
            { label: "Contact us", href: "/contact", icon: Mail },
        ],
    },
];

const mobileGroups: Record<string, MobileGroup[]> = {
    Tools: aiToolsMobile,
    Solutions: solutionsMobile,
    Resources: resourcesMobile,
};

interface NavbarProps {
    /** Pushes the nav down to make room for a fixed AnnouncementBanner above it. */
    offsetTop?: boolean;
}

const Navbar = ({ offsetTop = false }: NavbarProps) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [aiToolsOpen, setAiToolsOpen] = useState(false);
    const [aiTeamOpen, setAiTeamOpen] = useState(false);
    const [solutionsOpen, setSolutionsOpen] = useState(false);
    const [resourcesOpen, setResourcesOpen] = useState(false);
    const [openMobileSection, setOpenMobileSection] = useState<string | null>(
        null,
    );
    const location = useLocation();
    const pathname = location.pathname;
    const linkBase = "text-muted-foreground";
    const linkActive = "text-primary";

    const toolHrefs = [
        "/product-videos",
        "/talking-avatars",
        "/ugc-videos",
        "/upscale-video",
        "/caption-videos",
        "/fashion-reels",
        "/cgi-videos",
        "/background-replacer",
        "/product-showcase",
        "/virtual-try-on",
        "/ghost-mannequin",
        "/flat-lay",
        "/remove-background",
        "/upscale",
        "/resize",
        "/edit-with-ai",
    ];
    const resourceHrefs = ["/about", "/blogs", "/apis", "/contact"];

    const isToolsActive = toolHrefs.includes(pathname);
    const isAiTeamActive = pathname.startsWith("/ai-team/");
    const isSolutionsActive = pathname.startsWith("/solutions/");
    const isResourcesActive = resourceHrefs.some(
        (h) => pathname === h || pathname.startsWith(h + "/"),
    );
    const isPricingActive = pathname === "/pricing";

    const mobileSectionActive: Record<string, boolean> = {
        Tools: isToolsActive,
        "AI Team": isAiTeamActive,
        Solutions: isSolutionsActive,
        Resources: isResourcesActive,
    };

    const openMenu = (menu: "ai" | "team" | "solutions" | "resources") => {
        setAiToolsOpen(menu === "ai" ? !aiToolsOpen : false);
        setAiTeamOpen(menu === "team" ? !aiTeamOpen : false);
        setSolutionsOpen(menu === "solutions" ? !solutionsOpen : false);
        setResourcesOpen(menu === "resources" ? !resourcesOpen : false);
    };

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Lock body scroll while mobile sheet is open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    const closeMobile = () => {
        setMobileOpen(false);
        setOpenMobileSection(null);
    };

    return (
        <nav
            className={`fixed left-0 right-0 z-50 transition-[top] duration-300 ${offsetTop ? "top-10" : "top-0"}`}
        >
            {/* Blur layer is a sibling of the dropdowns, not their ancestor, so it doesn't trap their backdrop-filter in a rendering group */}
            <div
                className={`absolute inset-0 pointer-events-none transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled || mobileOpen ? "bg-card/40 backdrop-blur-2xl border-b border-white/10" : ""}`}
            />
            <div className="relative container mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
                <Link to="/" onClick={closeMobile}>
                    <img
                        src={pixroLogo}
                        alt="Pixro"
                        width={1139}
                        height={228}
                        className="h-3.5 w-auto"
                    />
                </Link>

                <div className="hidden items-center gap-7 lg:flex absolute left-1/2 -translate-x-1/2">
                    {navItems.map((item) => {
                        if (item.label === "Tools") {
                            return (
                                <div key={item.label} className="relative">
                                    <button
                                        type="button"
                                        onClick={() => openMenu("ai")}
                                        className={`flex items-center gap-1 text-sm transition-colors hover:text-foreground ${isToolsActive || aiToolsOpen ? linkActive : linkBase}`}
                                    >
                                        {item.label}
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform ${aiToolsOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <AiToolsMenu
                                        open={aiToolsOpen}
                                        onClose={() => setAiToolsOpen(false)}
                                    />
                                </div>
                            );
                        }
                        if (item.label === "AI Team") {
                            return (
                                <div key={item.label} className="relative">
                                    <button
                                        type="button"
                                        onClick={() => openMenu("team")}
                                        className={`flex items-center gap-1 text-sm transition-colors hover:text-foreground ${isAiTeamActive || aiTeamOpen ? linkActive : linkBase}`}
                                    >
                                        {item.label}
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform ${aiTeamOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <AiTeamMenu
                                        open={aiTeamOpen}
                                        onClose={() => setAiTeamOpen(false)}
                                    />
                                </div>
                            );
                        }
                        if (item.label === "Solutions") {
                            return (
                                <div key={item.label} className="relative">
                                    <button
                                        type="button"
                                        onClick={() => openMenu("solutions")}
                                        className={`flex items-center gap-1 text-sm transition-colors hover:text-foreground ${isSolutionsActive || solutionsOpen ? linkActive : linkBase}`}
                                    >
                                        {item.label}
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform ${solutionsOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <SolutionsMenu
                                        open={solutionsOpen}
                                        onClose={() => setSolutionsOpen(false)}
                                    />
                                </div>
                            );
                        }
                        if (item.label === "Resources") {
                            return (
                                <div key={item.label} className="relative">
                                    <button
                                        type="button"
                                        onClick={() => openMenu("resources")}
                                        className={`flex items-center gap-1 text-sm transition-colors hover:text-foreground ${isResourcesActive || resourcesOpen ? linkActive : linkBase}`}
                                    >
                                        {item.label}
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform ${resourcesOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <ResourcesMenu
                                        open={resourcesOpen}
                                        onClose={() => setResourcesOpen(false)}
                                    />
                                </div>
                            );
                        }
                        const to = "/pricing";
                        return (
                            <Link
                                key={item.label}
                                to={to}
                                className={`flex items-center gap-1 text-sm transition-colors hover:text-foreground ${isPricingActive ? linkActive : linkBase}`}
                            >
                                {item.label}
                                {item.hasDropdown && <ChevronDown size={14} />}
                            </Link>
                        );
                    })}
                </div>

                <div className="hidden items-center gap-3 lg:flex">
                    {/* <button className="rounded-lg border border-border px-5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:border-foreground/30">
                        Login
                    </button> */}
                    <button
                        type="button"
                        {...getCalTriggerProps(defaultPixroBookingConfig)}
                        className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.03] px-5 py-2 text-sm font-medium text-foreground/90 transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-foreground"
                    >
                        Book a call
                    </button>
                    <a
                        href={APP_URL}
                        className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-[filter] hover:brightness-110"
                    >
                        Get started
                        <ArrowRight size={14} />
                    </a>
                </div>

                <button
                    aria-label={mobileOpen ? "Close menu" : "Open menu"}
                    className="lg:hidden text-foreground"
                    onClick={() => setMobileOpen(!mobileOpen)}
                >
                    {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {mobileOpen && (
                <div className="relative lg:hidden border-t border-white/10 bg-card/40 backdrop-blur-2xl max-h-[calc(100vh-64px)] overflow-y-auto shadow-2xl">
                    <div className="flex flex-col px-4 sm:px-6 py-4">
                        {navItems.map((item) => {
                            if (item.hasDropdown) {
                                const isOpen = openMobileSection === item.label;
                                const groups = mobileGroups[item.label] ?? [];
                                const showGroupTitles = item.label === "Tools";
                                return (
                                    <div
                                        key={item.label}
                                        className="border-b border-border/40"
                                    >
                                        <button
                                            onClick={() =>
                                                setOpenMobileSection(
                                                    isOpen ? null : item.label,
                                                )
                                            }
                                            className={`flex w-full items-center justify-between py-4 text-left text-base ${mobileSectionActive[item.label] ? "text-primary" : "text-foreground"}`}
                                        >
                                            <span>{item.label}</span>
                                            <ChevronDown
                                                size={16}
                                                className={`text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
                                            />
                                        </button>
                                        {isOpen && (
                                            <div className="pb-4 pl-2 flex flex-col gap-4">
                                                {item.label === "AI Team" ? (
                                                    <div className="grid grid-cols-1 gap-1 pr-2">
                                                        {aiTeamAgents.map(
                                                            (agent) => (
                                                                <div
                                                                    key={
                                                                        agent.name
                                                                    }
                                                                    aria-disabled="true"
                                                                    className="flex items-center gap-3 rounded-lg px-3 py-2 cursor-not-allowed"
                                                                >
                                                                    <img
                                                                        src={
                                                                            agent.image
                                                                        }
                                                                        alt={
                                                                            agent.name
                                                                        }
                                                                        loading="lazy"
                                                                        width={
                                                                            160
                                                                        }
                                                                        height={
                                                                            160
                                                                        }
                                                                        className="h-10 w-10 shrink-0 rounded-full object-cover"
                                                                    />
                                                                    <div className="flex flex-col min-w-0 flex-1 gap-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-sm font-semibold text-foreground truncate">
                                                                                {
                                                                                    agent.name
                                                                                }
                                                                            </span>
                                                                            <span className="shrink-0 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                                                                Coming
                                                                                soon
                                                                            </span>
                                                                        </div>
                                                                        <span className="text-xs text-muted-foreground truncate">
                                                                            {
                                                                                agent.role
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ),
                                                        )}
                                                        <Link
                                                            to="/ai-team"
                                                            onClick={
                                                                closeMobile
                                                            }
                                                            className="mt-2 flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-card/40 py-2.5 text-sm font-medium text-foreground hover:text-primary"
                                                        >
                                                            Meet the full crew
                                                        </Link>
                                                    </div>
                                                ) : (
                                                    groups.map((group) => (
                                                        <div key={group.title}>
                                                            {showGroupTitles && (
                                                                <h4 className="text-[11px] font-semibold tracking-widest text-muted-foreground px-3 mb-1">
                                                                    {group.title.toUpperCase()}
                                                                </h4>
                                                            )}
                                                            <ul className="flex flex-col gap-0.5">
                                                                {group.links.map(
                                                                    (link) => {
                                                                        const Icon =
                                                                            link.icon;
                                                                        const isInternal =
                                                                            link.href.startsWith(
                                                                                "/",
                                                                            );
                                                                        const comingSoonClass =
                                                                            "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground/40 hover:bg-muted/40 hover:text-foreground/60 transition-colors";
                                                                        const linkClass = `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm hover:bg-muted/40 ${pathname === link.href || pathname.startsWith(link.href + "/") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`;
                                                                        return (
                                                                            <li
                                                                                key={
                                                                                    link.href
                                                                                }
                                                                            >
                                                                                {link.comingSoon ? (
                                                                                    isInternal ? (
                                                                                        <Link
                                                                                            to={
                                                                                                link.href
                                                                                            }
                                                                                            onClick={
                                                                                                closeMobile
                                                                                            }
                                                                                            className={
                                                                                                comingSoonClass
                                                                                            }
                                                                                        >
                                                                                            <Icon className="h-4 w-4 shrink-0" />
                                                                                            <span className="flex-1">
                                                                                                {
                                                                                                    link.label
                                                                                                }
                                                                                            </span>
                                                                                            <span className="text-[10px] font-medium tracking-wide bg-muted/60 text-muted-foreground rounded px-1.5 py-0.5">
                                                                                                Soon
                                                                                            </span>
                                                                                        </Link>
                                                                                    ) : (
                                                                                        <a
                                                                                            href={
                                                                                                link.href
                                                                                            }
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            onClick={
                                                                                                closeMobile
                                                                                            }
                                                                                            className={
                                                                                                comingSoonClass
                                                                                            }
                                                                                        >
                                                                                            <Icon className="h-4 w-4 shrink-0" />
                                                                                            <span className="flex-1">
                                                                                                {
                                                                                                    link.label
                                                                                                }
                                                                                            </span>
                                                                                            <span className="text-[10px] font-medium tracking-wide bg-muted/60 text-muted-foreground rounded px-1.5 py-0.5">
                                                                                                Soon
                                                                                            </span>
                                                                                        </a>
                                                                                    )
                                                                                ) : !isInternal ? (
                                                                                    <a
                                                                                        href={
                                                                                            link.href
                                                                                        }
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        onClick={
                                                                                            closeMobile
                                                                                        }
                                                                                        className={
                                                                                            linkClass
                                                                                        }
                                                                                    >
                                                                                        <Icon className="h-4 w-4 shrink-0" />
                                                                                        <span>
                                                                                            {
                                                                                                link.label
                                                                                            }
                                                                                        </span>
                                                                                    </a>
                                                                                ) : (
                                                                                    <Link
                                                                                        to={
                                                                                            link.href
                                                                                        }
                                                                                        onClick={
                                                                                            closeMobile
                                                                                        }
                                                                                        className={
                                                                                            linkClass
                                                                                        }
                                                                                    >
                                                                                        <Icon className="h-4 w-4 shrink-0" />
                                                                                        <span>
                                                                                            {
                                                                                                link.label
                                                                                            }
                                                                                        </span>
                                                                                    </Link>
                                                                                )}
                                                                            </li>
                                                                        );
                                                                    },
                                                                )}
                                                            </ul>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            const to = "/pricing";
                            return (
                                <Link
                                    key={item.label}
                                    to={to}
                                    onClick={closeMobile}
                                    className={`block border-b border-border/40 py-4 text-base ${isPricingActive ? "text-primary" : "text-foreground"}`}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}
                        <div className="mt-6 flex flex-col gap-3">
                            {/* <button className="w-full rounded-lg border border-border px-5 py-3 text-sm text-foreground">
                Login
              </button> */}
                            <button
                                type="button"
                                {...getCalTriggerProps(
                                    defaultPixroBookingConfig,
                                )}
                                className="flex w-full items-center justify-center rounded-lg border border-white/15 bg-white/[0.03] px-5 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/10"
                            >
                                Book a call
                            </button>
                            <a
                                href={APP_URL}
                                className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-5 py-3 text-sm font-medium text-primary-foreground"
                            >
                                Get started <ArrowRight size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
